import React from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { useNumericFocusBehavior } from '../utils/useNumericFocusBehavior';

/**
 * MobileNumberField
 *
 * Drop-in replacement for the raw `<TextField type="number" value={...}
 * onChange={...} />` used throughout the mobile card edit dialogs.
 *
 * It fixes the leading-zero UX problem: tapping into a field that shows "0"
 * (or "$ 0") previously forced the user to delete the zero first, producing
 * values like "05" / "010" / "01". Now the field auto-selects its contents on
 * focus, shows blank when the value is zero, and restores to 0 on blur if left
 * empty. The shared behavior lives in `useNumericFocusBehavior` so every
 * numeric field behaves identically.
 *
 * All other TextField props (label, size, fullWidth, InputProps adornments,
 * etc.) pass straight through, so call sites only change the tag name.
 */
type MobileNumberFieldProps = Omit<TextFieldProps, 'onChange'> & {
  value: number | string | null | undefined;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

export const MobileNumberField: React.FC<MobileNumberFieldProps> = ({
  value,
  onChange,
  onFocus,
  onBlur,
  type = 'number',
  ...props
}) => {
  const behavior = useNumericFocusBehavior(
    value,
    onChange,
    onFocus as ((e: React.FocusEvent<HTMLInputElement>) => void) | undefined,
    onBlur as ((e: React.FocusEvent<HTMLInputElement>) => void) | undefined,
  );

  return (
    <TextField
      {...props}
      type={type}
      value={behavior.value}
      onFocus={behavior.onFocus}
      onChange={behavior.onChange as TextFieldProps['onChange']}
      onBlur={behavior.onBlur}
    />
  );
};
